// Barrel for the tariff feature.
//
// Re-exports the generated API types (the single source of truth, kept in sync by
// `npm run generate:api:operator`) so feature code imports them from one place, plus
// the presentation-only helpers that are not part of the API.
export type {
  TariffDto,
  TariffRuleDto,
  TariffVersionDto,
  PricingPolicyDto,
  TariffConditionDto,
  RateTierDto,
  RateCapDto,
  RateBandDto,
  GracePeriodDto,
  CreateTariffRequest,
  UpdateTariffRequest,
  AddTariffRuleRequest,
  EditTariffRuleRequest,
  AddTariffConditionRequest,
  AddTariffVersionRequest,
  AddGracePeriodRequest,
  AddRateCapRequest,
  AddRateTierRequest,
  AddRateBandRequest,
} from '../../../api/gen';

export {
  TariffStatus,
  RuleEffect,
  ParkerClassification,
  ConditionKind,
  ConditionOperator,
  BillingInterval,
  TimeSeriesType,
  GraceTrigger,
  GraceBehavior,
  CapWindow,
  CapAnchor,
} from '../../../api/gen';

import {
  BillingInterval,
  CapAnchor,
  CapWindow,
  ConditionKind,
  ConditionOperator,
  GraceBehavior,
  GraceTrigger,
  ParkerClassification,
  RuleEffect,
  TariffStatus,
  TimeSeriesType,
} from '../../../api/gen';

export const TARIFF_STATUSES: TariffStatus[] = [
  TariffStatus.Draft,
  TariffStatus.Active,
  TariffStatus.Archived,
];

export const RULE_EFFECTS: RuleEffect[] = [RuleEffect.Include, RuleEffect.Exclude];

/** Selectable classifications for a rule (Unspecified is represented as "no classification"). */
export const RULE_CLASSIFICATIONS: ParkerClassification[] = [
  ParkerClassification.Employee,
  ParkerClassification.Student,
  ParkerClassification.Visitor,
  ParkerClassification.Contractor,
  ParkerClassification.Other,
];

/** TimeOfDay / DayOfWeek are deliberately not offered: the engine does not evaluate rule conditions
 *  when it selects a tariff, so a "night tariff 19–06" authored as a condition saves fine and never
 *  applies. Time-of-day pricing is a rate band on the version instead (RateBandDraft). */
export const CONDITION_KINDS: ConditionKind[] = [
  ConditionKind.DateRange,
  ConditionKind.LicensePlate,
  ConditionKind.LicensePlateRegex,
  ConditionKind.Event,
];

export const CONDITION_OPERATORS: ConditionOperator[] = [
  ConditionOperator.Equals,
  ConditionOperator.In,
  ConditionOperator.NotIn,
  ConditionOperator.Between,
  ConditionOperator.Gte,
  ConditionOperator.Lte,
  ConditionOperator.Matches,
];

export const BILLING_INTERVALS: BillingInterval[] = [
  BillingInterval.Minute,
  BillingInterval.Hour,
  BillingInterval.Day,
];

export const TIME_SERIES_TYPES: TimeSeriesType[] = [
  TimeSeriesType.DurationBased,
  TimeSeriesType.FixedTimeBased,
];

export const GRACE_TRIGGERS: GraceTrigger[] = [GraceTrigger.EntryStart, GraceTrigger.ExitDeadline];

export const GRACE_BEHAVIORS: GraceBehavior[] = [
  GraceBehavior.FreeThenFullRate,
  GraceBehavior.RoundDownToZero,
  GraceBehavior.Discounted,
];

/** Presentation-only: a "field: value" chip describing part of a rule's scope.
 *  `labelKey` is the i18n key suffix under `tariff.field.*`. */
export interface RuleScopeField {
  labelKey: string;
  value: string;
  /** Groups the chip: `ladder` = one of the three matching levels that decide which rule wins
   *  (category / product / grant), `parker` = who the parker is, `location` = where they are. */
  tone: 'ladder' | 'parker' | 'location';
}

/** A condition staged locally in the rule wizard before the rule (and its conditions) are submitted. */
export interface RuleConditionDraft {
  kind: ConditionKind;
  operator: ConditionOperator;
  value: string;
  valueTo?: string | null;
  negate: boolean;
}

/** A grace period staged locally in the version wizard before submission. */
export interface GracePeriodDraft {
  durationMinutes: number;
  trigger: GraceTrigger;
  behavior: GraceBehavior;
}

export const CAP_WINDOWS: CapWindow[] = [CapWindow.Daily, CapWindow.Weekly, CapWindow.Monthly];

/** v4.5 D-42: the only anchor operators may author. `CapAnchor.CalendarDay` needs a pricing
 *  timezone that `PricingPolicy` does not carry — the evaluator would fall back to UTC, resetting a
 *  daily cap at 01:00/02:00 Austrian local time. The server refuses it too; this is not UI-only.
 *  A rolling window starts at the parker's own entry, so no timezone is involved. */
export const AUTHORABLE_CAP_ANCHOR: CapAnchor = CapAnchor.RollingFromEntry;

/** A rate tier (Rabattstufe) staged locally in the version wizard before submission.
 *  Currency and evaluation order are derived server-side from the policy — never sent. */
export interface RateTierDraft {
  afterMinutes: number;
  newAmount: number;
}

/** A rate cap staged locally in the version wizard before submission.
 *  Currency comes from the policy's base currency, server-side. */
export interface RateCapDraft {
  window: CapWindow;
  maxAmount: number;
  windowAnchor: CapAnchor;
}

/** Time zone new versions read their rate bands in. Every site today is in Austria. */
export const DEFAULT_TARIFF_TIME_ZONE = 'Europe/Vienna';

/** A time-of-day rate band staged locally in the version wizard before submission.
 *  Times are "HH:mm" as the time input gives them; currency comes from the policy, server-side. */
export interface RateBandDraft {
  startTime: string;
  endTime: string;
  amount: number;
}
